import React, { useMemo, useState } from 'react';
import { useStore } from '../state/store';
import { Card, Chip, Empty, Text } from '../components/ui';
import { derivePlanStrategyMap } from '../lib/planStrategyMap';
import { buildJiraBrowseUrl } from '../lib/jira';
import { buildJiraUrlFromEpic, planNeedsSetup } from '../lib/orgPlan';
import PlanSetup from '../components/PlanSetup';

const RISK_TONE = {
  Healthy: 'green',
  'At Risk': 'amber',
  Violated: 'red',
  'Not assessable': '',
};

function MetaRow({ label, children }) {
  return (
    <div className="plan-meta-row">
      <span className="plan-meta-label">{label}</span>
      <span className="plan-meta-value">{children}</span>
    </div>
  );
}

function patchEpic(set, epics, epicId, changes) {
  set(
    'plan.epics',
    epics.map((e) => (e.id === epicId ? { ...e, ...changes } : e)),
  );
}

export default function Plan() {
  const { state, d, set, reload } = useStore();
  const [openEpic, setOpenEpic] = useState(null);
  const needsSetup = planNeedsSetup(state);

  const epics = state.plan?.epics || [];
  const jiraBaseUrl = state.settings?.jira?.baseUrl || '';
  const map = useMemo(() => derivePlanStrategyMap(state, d), [state, d]);
  const { progression } = map;

  if (needsSetup && !epics.length) {
    return <PlanSetup onDone={() => reload?.()} />;
  }

  return (
    <>
      <div className="page-head">
        <div className="row">
          <h2>45-Day Strategy Map</h2>
          <Chip>
            {map.epics.length} epic{map.epics.length === 1 ? '' : 's'}
          </Chip>
          {d.sprint.nextGate && (
            <Chip tone="accent">
              {map.nextGateLabel}
              {map.daysToGate !== null
                ? ` · ${map.daysToGate} day${map.daysToGate === 1 ? '' : 's'} remaining`
                : ''}
            </Chip>
          )}
        </div>
        <div className="sub">
          Epics are the map. Gates are the contracts. Work Packages organize the effort. Founder Moves
          drive today. Evidence determines progress. Jira owns detailed tasks — open them externally
          via issue key.
        </div>
      </div>

      {map.epics.length === 0 ? (
        <Empty title="No epics in the organization plan.">
          Complete plan setup or migrate an existing outline.
        </Empty>
      ) : (
        <>
          <section className="section">
            <Card title="How work progresses">
              <MetaRow label="What does the next gate require?">{progression.gateRequires}</MetaRow>
              <MetaRow label="What outcomes are currently missing?">
                {progression.missingOutcomes.length
                  ? progression.missingOutcomes
                      .slice(0, 3)
                      .map((o) => o.requiredOutcome)
                      .join(' · ')
                  : 'None named — inspect Epic gate risk below.'}
              </MetaRow>
              <MetaRow label="Which Work Package owns each gap?">
                {progression.owningPackages.length
                  ? progression.owningPackages
                      .slice(0, 4)
                      .map((p) => p.title)
                      .join(' · ')
                  : 'No active Work Package on a gap.'}
              </MetaRow>
              <MetaRow label="What is the next executable Founder Move?">
                {progression.nextMove
                  ? `${progression.nextMove.text}${
                      progression.nextMove.expectedEvidence
                        ? ` · expected ${progression.nextMove.expectedEvidence}`
                        : ''
                    }`
                  : 'No Ready or Today move recorded.'}
              </MetaRow>
            </Card>
          </section>

          <section className="section">
            <div className="grid g2">
              {map.epics.map((e) => {
                const expanded = openEpic === e.id;
                const live = epics.find((x) => x.id === e.id) || e;
                const href =
                  buildJiraUrlFromEpic(jiraBaseUrl, live) ||
                  buildJiraBrowseUrl(jiraBaseUrl, live.jiraSpaceKey, live.jiraId);
                return (
                  <Card
                    key={e.id}
                    title={`${e.id} — ${e.title}`}
                    right={
                      <div className="row tight">
                        <Chip tone={RISK_TONE[e.gateRisk] || ''}>{e.gateRisk}</Chip>
                        <button
                          className="btn sm ghost"
                          onClick={() => setOpenEpic(expanded ? null : e.id)}
                        >
                          {expanded ? 'Collapse' : 'Expand'}
                        </button>
                      </div>
                    }
                  >
                    <MetaRow label="Status">{e.strategyStatus}</MetaRow>
                    <MetaRow label="Next gate contribution">
                      {e.nextGateDay ? `Day ${e.nextGateDay}` : 'None this gate'}
                    </MetaRow>
                    <MetaRow label="Required outcome">{e.requiredOutcome}</MetaRow>
                    <MetaRow label="Active work packages">{e.activeWorkPackageCount}</MetaRow>
                    <MetaRow label="Evidence generated">{e.evidenceLabel}</MetaRow>
                    <MetaRow label="Gate risk">
                      <Chip tone={RISK_TONE[e.gateRisk] || ''}>{e.gateRisk}</Chip>
                    </MetaRow>

                    {expanded && (
                      <>
                        <div className="hr" />
                        {(e.evidenceRequired || e.acceptance) && (
                          <div className="dim tiny" style={{ marginBottom: 10 }}>
                            {e.evidenceRequired && <div>Locked evidence: {e.evidenceRequired}</div>}
                            {e.acceptance && <div>Acceptance: {e.acceptance}</div>}
                          </div>
                        )}

                        <div className="section-head">
                          <h3>Active Work Packages</h3>
                        </div>
                        {e.activeWorkPackages.length === 0 ? (
                          <Empty>
                            No active Work Package for this gate. Jira may still hold implementation
                            work outside Bridge45.
                          </Empty>
                        ) : (
                          e.activeWorkPackages.map((pkg) => (
                            <div key={pkg.id} style={{ marginTop: 12 }}>
                              <div className="row tight">
                                <strong>Work Package: {pkg.title}</strong>
                                <Chip>{pkg.status}</Chip>
                              </div>
                              <MetaRow label="Outcome">{pkg.completionCondition || '—'}</MetaRow>
                              <MetaRow label="Current state">{pkg.currentState}</MetaRow>
                              <MetaRow label="Current constraint">{pkg.constraint || '—'}</MetaRow>
                              <MetaRow label="Open founder moves">{pkg.openMoveCount}</MetaRow>
                              {pkg.openMoves.length > 0 && (
                                <ul className="dim tiny" style={{ margin: '6px 0 0 18px' }}>
                                  {pkg.openMoves.map((m) => (
                                    <li key={m.id}>
                                      {m.text} · {m.status}
                                    </li>
                                  ))}
                                </ul>
                              )}
                              <MetaRow label="Evidence">{pkg.evidenceLabel}</MetaRow>
                            </div>
                          ))
                        )}

                        <div className="hr" />
                        <div className="section-head">
                          <h3>Jira implementation reference</h3>
                        </div>
                        <div className="dim tiny" style={{ marginBottom: 10 }}>
                          Not the Bridge45 backlog. Set the issue key (or space key + id) so the
                          founder can open the epic externally. Stories stay in Jira.
                        </div>
                        <div className="grid g2">
                          <Text
                            label="Issue key"
                            value={live.jiraIssueKey || ''}
                            onChange={(v) => {
                              const key = String(v || '').trim().toUpperCase();
                              const parts = key.includes('-') ? key.split('-') : [];
                              patchEpic(set, epics, e.id, {
                                jiraIssueKey: key,
                                jiraSpaceKey: parts[0] || live.jiraSpaceKey || '',
                                jiraId: parts[1] || live.jiraId || '',
                              });
                            }}
                            placeholder="B45-123"
                          />
                          <Text
                            label="Space key"
                            value={live.jiraSpaceKey || ''}
                            onChange={(v) =>
                              patchEpic(set, epics, e.id, {
                                jiraSpaceKey: String(v || '').trim().toUpperCase(),
                              })
                            }
                            placeholder="B45"
                          />
                          <Text
                            label="Jira ID"
                            value={live.jiraId || ''}
                            onChange={(v) =>
                              patchEpic(set, epics, e.id, { jiraId: String(v || '').trim() })
                            }
                            placeholder="123"
                          />
                        </div>
                        <div className="row tight" style={{ marginTop: 8 }}>
                          {href ? (
                            <a className="btn sm" href={href} target="_blank" rel="noreferrer">
                              Open in Jira
                            </a>
                          ) : (
                            <span className="dim tiny">
                              {jiraBaseUrl
                                ? 'Enter issue key (or space key and Jira ID) to enable the link.'
                                : 'Set the Jira base URL in Settings to enable Open in Jira.'}
                            </span>
                          )}
                          {href && (
                            <span className="dim tiny mono">
                              {href.replace(/^https?:[/][/]/, '')}
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </Card>
                );
              })}
            </div>
          </section>
        </>
      )}
    </>
  );
}
